import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getHeroBannersList,
  deleteHeroBanner,
} from "../../../services/heroBannersService";
import { getHeroBannerButtonsList } from "../../../services/heroBannerButtonsService";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../../Common/TableComponent/tableBadges";
import { Pagination } from "../../Common/TableComponent/Pagination";
import TableToolbar from "../../Common/TableComponent/TableToolbar";
import { AddHeroBanner } from "./AddHeroBanner";
import { Loading } from "../../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import allImages from "../../../assets/images-import";
import { BANNER_TYPE_OPTIONS } from "./heroBannerConstants";
import {
  getBannerId,
  getBannerName,
  getBannerType,
  getBannerTitle,
  getDisplayOrder,
  getDesktopImageUrl,
  extractListResult,
  filterButtonsByBannerId,
} from "./heroBannerUtils";

const renderThumbnail = (url) => (
  <img
    src={url || allImages.DefultImage}
    alt="Banner"
    className="rounded img-thumbnail"
    style={{ width: 48, height: 32, objectFit: "cover" }}
    onError={(e) => {
      e.target.onerror = null;
      e.target.src = allImages.DefultImage;
    }}
  />
);

const getTypeLabel = (type) => {
  const match = BANNER_TYPE_OPTIONS.find((opt) => opt.value === type);
  return match?.label || type || "-";
};

export const ManageHeroBannersContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "hero-banners";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [bannersList, setBannersList] = useState([]);
  const [allButtons, setAllButtons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bannersRes, buttonsRes] = await Promise.all([
        getHeroBannersList(),
        getHeroBannerButtonsList(),
      ]);
      setBannersList(extractListResult(bannersRes));
      setAllButtons(extractListResult(buttonsRes));
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = bannersList.filter((item) => {
    const name = getBannerName(item).toLowerCase();
    const title = getBannerTitle(item).toLowerCase();
    const type = getBannerType(item);
    const query = searchQuery.toLowerCase();
    const matchesSearch = name.includes(query) || title.includes(query);
    const matchesType = typeFilter === "all" || type === typeFilter;
    return matchesSearch && matchesType;
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (bannerId) => {
    const confirmed = await confirmDelete("Hero Banner");
    if (confirmed) {
      try {
        await deleteHeroBanner(bannerId);
        Swal.fire(
          "Deleted!",
          "The hero banner has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  return (
    <>
      {pageAccessDetails.addAccess ? (
        <AddHeroBanner
          editMode={editMode}
          initialData={selectedBanner ?? {}}
          onSuccess={fetchData}
          setSelectedBanner={setSelectedBanner}
          setEditMode={setEditMode}
        />
      ) : (
        ""
      )}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-sm-2 mt-sm-2">Manage Hero Banners</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search banners..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  onRefresh={fetchData}
                  left={
                    <select
                      className="form-select form-select-sm xc-table-filter-select"
                      value={typeFilter}
                      onChange={(e) => {
                        setTypeFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="all">All Types</option>
                      {BANNER_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  }
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Banner Image",
                          "Banner Name",
                          "Banner Type",
                          "Banner Title",
                          "Display Order",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="8" />
                        ) : (
                          currentData.map((item, index) => {
                            const bannerId = getBannerId(item);
                            const buttonCount = filterButtonsByBannerId(
                              allButtons,
                              bannerId
                            ).length;
                            return (
                              <TablesRow
                                key={bannerId}
                                rowData={{
                                  slNo:
                                    (currentPage - 1) * entriesPerPage +
                                    index +
                                    1,
                                  image: renderThumbnail(
                                    getDesktopImageUrl(item)
                                  ),
                                  bannerName: (
                                    <>
                                      {getBannerName(item)}
                                      {buttonCount > 0 && (
                                        <small className="d-block text-muted">
                                          {buttonCount} button
                                          {buttonCount !== 1 ? "s" : ""}
                                        </small>
                                      )}
                                    </>
                                  ),
                                  bannerType: getTypeLabel(
                                    getBannerType(item)
                                  ),
                                  bannerTitle: getBannerTitle(item),
                                  displayOrder: getDisplayOrder(item),
                                  status: renderStatusBadge(item),
                                }}
                                columns={[
                                  "slNo",
                                  "image",
                                  "bannerName",
                                  "bannerType",
                                  "bannerTitle",
                                  "displayOrder",
                                  "status",
                                ]}
                                hideIcons={false}
                                onEdit={() => {
                                  setSelectedBanner(item);
                                  setEditMode(true);
                                  window.scrollTo({
                                    top: 0,
                                    behavior: "smooth",
                                  });
                                }}
                                onDelete={() => handleDelete(bannerId)}
                                pageLevelAccessData={pageAccessDetails}
                              />
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
